/**
 * Broadcast API client — V3 Phase 15 (marketing automation). Typed wrapper over
 * app/api/v1/broadcast.py:
 *
 *   POST /broadcast/campaigns                       — broadcastApi.createCampaign
 *   GET  /broadcast/campaigns[?status=]             — broadcastApi.listCampaigns
 *   GET  /broadcast/campaigns/{id}                  — broadcastApi.getCampaign
 *   POST /broadcast/campaigns/{id}/status           — broadcastApi.setStatus
 *   POST /broadcast/campaigns/{id}/dispatch         — broadcastApi.dispatch
 *   POST /broadcast/contacts                        — broadcastApi.createContact
 *   GET  /broadcast/contacts                        — broadcastApi.listContacts
 *   POST /broadcast/audiences                       — broadcastApi.createAudience
 *   POST /broadcast/audiences/{id}/members          — broadcastApi.addAudienceMember
 *   POST /broadcast/campaigns/{id}/dispatch-audience— broadcastApi.dispatchToAudience
 *   POST /broadcast/campaigns/{id}/schedule         — broadcastApi.scheduleCampaign
 *   POST /broadcast/campaigns/{id}/cancel-schedule  — broadcastApi.cancelSchedule
 *   GET  /broadcast/campaigns/{id}/jobs             — broadcastApi.listJobs
 *
 * The backend is ORG-scoped (get_org_context), NOT project-scoped in the path —
 * an optional project_id travels in the create body. Mutations are role-gated
 * (PROJECT_MANAGER+) server-side; these helpers do NOT self-gate. The Broadcast
 * surface + the route mounting it are flag-gated by `ftl.ux.v3.broadcast_mode`
 * (matching the operateApi / hostingApi pattern).
 */

import { apiClient } from "@/api/client";
import type {
  BroadcastAudience,
  BroadcastAudienceMemberResult,
  BroadcastAudiencesResponse,
  BroadcastCampaign,
  BroadcastCampaignAudienceResult,
  BroadcastCampaignReport,
  BroadcastCampaignsResponse,
  BroadcastCancelScheduleResult,
  CampaignNote,
  BroadcastChannel,
  BroadcastContact,
  BroadcastContactsResponse,
  BroadcastContentUpdate,
  BroadcastDispatchResult,
  BroadcastFlow,
  BroadcastFlowStep,
  BroadcastFlowTrigger,
  BroadcastImportResult,
  BroadcastJobsResponse,
  BroadcastMetrics,
  BroadcastReadiness,
  BroadcastEligibility,
  BroadcastScheduleInput,
  BroadcastSendJob,
  BroadcastStatus,
  BroadcastStatusUpdateResult,
  BroadcastVariant,
  BroadcastVariantInput,
} from "@/api/types";

/** A single segmentation rule. `field` is one of: subscribed | email | first_name |
 *  last_name | source | tag | attr:<key>. Ops depend on the field (see segment_service). */
export interface SegmentRule {
  field: string;
  op: string;
  value?: string | number | boolean | null;
}

export interface SegmentRuleSet {
  match: "all" | "any";
  rules: SegmentRule[];
}

export interface AnalyticsOverview {
  window_days: number;
  campaigns: number;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  unsubscribed: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
  click_to_open_rate: number;
}

export interface CampaignPerf {
  campaign_id: string | null;
  name: string | null;
  sent: number;
  delivered: number;
  opened: number;
  clicked: number;
  open_rate: number;
  click_rate: number;
  last_dispatch: string | null;
}

export interface HourBucket {
  hour: number;
  opens: number;
  clicks: number;
}

export interface TopLink {
  link: string;
  clicks: number;
}

export interface MarketingAnalytics {
  overview: AnalyticsOverview;
  by_campaign: CampaignPerf[];
  time_of_day: HourBucket[];
  top_links: TopLink[];
}

export interface CampaignTemplate {
  id: string;
  name: string;
  description: string | null;
  category: string;
  channel: string;
  subject: string | null;
  body_template: string | null;
  content_json: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface CreateTemplateInput {
  name: string;
  description?: string | null;
  category?: string;
  subject?: string | null;
  body_template?: string | null;
  content_json?: Record<string, unknown>;
}

export interface CreateCampaignInput {
  name: string;
  channel?: BroadcastChannel;
  project_id?: string | null;
  description?: string | null;
  subject?: string | null;
  body_template?: string | null;
}

export interface CreateContactInput {
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  tags?: string[];
  source?: string | null;
}

export interface ImportContactInput {
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  tags?: string[];
}

export interface CreateAudienceInput {
  name: string;
  description?: string | null;
  filter_json?: Record<string, unknown>;
}

// ── Content epic — AI-drafted copy + saved snippets ────────────────────────

export type ContentKind = "caption" | "post_idea" | "subject_line" | "ad_copy";

export interface GenerateContentInput {
  kind: ContentKind;
  topic: string;
  tone?: string | null;
  channel?: string | null;
  count?: number;
  /** Ground the copy in this project's CONFIRMED outcome record — its
   *  statement, quality bar and acceptance criteria. The endpoint has accepted
   *  this since the content epic landed and no caller ever sent it, so every
   *  draft was ungrounded. Omitted, or a project with no ratified outcome,
   *  generates ungrounded and the response says which. */
  project_id?: string | null;
}

export interface GeneratedContent {
  kind: ContentKind;
  topic: string;
  tone: string | null;
  channel: string | null;
  variants: string[];
  /** Whether the copy was written from the project's ratified claims. Explicit
   *  rather than inferred: grounded and ungrounded output look identical, and
   *  the service returns this flag precisely so a caller cannot mistake one
   *  for the other. */
  grounded?: boolean;
}

export interface ContentSnippet {
  id: string;
  kind: ContentKind;
  topic: string | null;
  tone: string | null;
  channel: string | null;
  body: string;
  created_by: string | null;
  created_at: string;
}

export interface SaveSnippetInput {
  kind: ContentKind;
  body: string;
  topic?: string | null;
  tone?: string | null;
  channel?: string | null;
}

// ── Approval / governance (migration 503) ──────────────────────────────────

export type ApprovalStatus = "NONE" | "PENDING" | "APPROVED" | "REJECTED";

export interface ApprovalHistoryEntry {
  action: "REQUESTED" | "APPROVED" | "REJECTED";
  actor_id: string | null;
  note: string | null;
  created_at: string;
}

export interface CampaignApproval {
  campaign_id: string;
  status: ApprovalStatus;
  actor_id: string | null;
  note: string | null;
  updated_at: string | null;
  required: boolean;
  history: ApprovalHistoryEntry[];
}

export interface PendingApproval {
  campaign_id: string;
  name: string | null;
  requested_by: string | null;
  requested_at: string;
}

export const broadcastApi = {
  /** POST /broadcast/campaigns — create a DRAFT campaign. */
  createCampaign(input: CreateCampaignInput): Promise<BroadcastCampaign> {
    return apiClient.post<BroadcastCampaign>("/broadcast/campaigns", input);
  },

  /** GET /broadcast/campaigns — org's campaigns, newest first; optional status filter. */
  listCampaigns(status?: BroadcastStatus): Promise<BroadcastCampaignsResponse> {
    return apiClient.get<BroadcastCampaignsResponse>(
      "/broadcast/campaigns",
      status ? { status } : undefined,
    );
  },

  /** GET /broadcast/campaigns/{id} — single campaign (404 if not in org). */
  getCampaign(campaignId: string): Promise<BroadcastCampaign> {
    return apiClient.get<BroadcastCampaign>(`/broadcast/campaigns/${campaignId}`);
  },

  /** POST /broadcast/campaigns/{id}/brief — set the campaign's free-text brief. */
  setCampaignBrief(campaignId: string, brief: string): Promise<{ id: string; brief: string | null }> {
    return apiClient.post(`/broadcast/campaigns/${campaignId}/brief`, { brief });
  },

  /** POST /broadcast/campaigns/{id}/notes — append a team note (append-only). */
  addCampaignNote(
    campaignId: string,
    text: string,
  ): Promise<{ campaign_id: string; notes: CampaignNote[] }> {
    return apiClient.post(`/broadcast/campaigns/${campaignId}/notes`, { text });
  },

  /** GET /broadcast/campaigns/{id}/approval — current state + audit trail + required. */
  getCampaignApproval(campaignId: string): Promise<CampaignApproval> {
    return apiClient.get<CampaignApproval>(`/broadcast/campaigns/${campaignId}/approval`);
  },

  /** POST /broadcast/campaigns/{id}/request-approval — submit for sign-off. */
  requestCampaignApproval(campaignId: string): Promise<{ status: ApprovalStatus }> {
    return apiClient.post(`/broadcast/campaigns/${campaignId}/request-approval`, {});
  },

  /** POST /broadcast/campaigns/{id}/approve — approve (a different teammate). */
  approveCampaign(campaignId: string, note?: string): Promise<{ status: ApprovalStatus }> {
    return apiClient.post(`/broadcast/campaigns/${campaignId}/approve`, { note: note ?? null });
  },

  /** POST /broadcast/campaigns/{id}/reject — reject with an optional note. */
  rejectCampaign(campaignId: string, note?: string): Promise<{ status: ApprovalStatus }> {
    return apiClient.post(`/broadcast/campaigns/${campaignId}/reject`, { note: note ?? null });
  },

  /** GET /broadcast/campaigns/approvals/pending — the reviewer queue. */
  listPendingApprovals(): Promise<{ count: number; pending: PendingApproval[] }> {
    return apiClient.get("/broadcast/campaigns/approvals/pending");
  },

  /** POST /broadcast/campaigns/{id}/status — transition campaign status. */
  setStatus(
    campaignId: string,
    status: BroadcastStatus,
  ): Promise<BroadcastStatusUpdateResult> {
    return apiClient.post<BroadcastStatusUpdateResult>(
      `/broadcast/campaigns/${campaignId}/status`,
      { status },
    );
  },

  /** POST /broadcast/campaigns/{id}/dispatch — send to an explicit recipient list
   *  (email channel only; nothing auto-invokes this — deliberate, role-gated). */
  dispatch(
    campaignId: string,
    recipients: string[],
    overrideDeliverabilityFloor = false,
  ): Promise<BroadcastDispatchResult> {
    return apiClient.post<BroadcastDispatchResult>(
      `/broadcast/campaigns/${campaignId}/dispatch`,
      { recipients, override_deliverability_floor: overrideDeliverabilityFloor },
    );
  },

  // ── Contacts (migration 491) ──────────────────────────────────────────────

  /** POST /broadcast/contacts — add or upsert a contact to the org's book. */
  createContact(input: CreateContactInput): Promise<BroadcastContact> {
    return apiClient.post<BroadcastContact>("/broadcast/contacts", input);
  },

  /** GET /broadcast/contacts — the org's contact book, newest first. */
  listContacts(limit = 200): Promise<BroadcastContactsResponse> {
    return apiClient.get<BroadcastContactsResponse>("/broadcast/contacts", { limit });
  },

  /** POST /broadcast/contacts/{id}/resubscribe — clear a contact's suppression
   *  (deliberate override; the org owns the deliverability risk). */
  resubscribeContact(contactId: string): Promise<{ contact: BroadcastContact }> {
    return apiClient.post<{ contact: BroadcastContact }>(
      `/broadcast/contacts/${contactId}/resubscribe`,
      {},
    );
  },

  /** POST /broadcast/contacts/import — bulk import (validated + de-duped server-side). */
  importContacts(
    contacts: ImportContactInput[],
    source = "import",
  ): Promise<BroadcastImportResult> {
    return apiClient.post<BroadcastImportResult>("/broadcast/contacts/import", {
      contacts,
      source,
    });
  },

  // ── Audiences (migrations 405 + 491) ──────────────────────────────────────

  /** POST /broadcast/audiences — create a named audience. */
  createAudience(input: CreateAudienceInput): Promise<BroadcastAudience> {
    return apiClient.post<BroadcastAudience>("/broadcast/audiences", input);
  },

  /** GET /broadcast/audiences — the org's audiences with live subscribed counts. */
  listAudiences(): Promise<BroadcastAudiencesResponse> {
    return apiClient.get<BroadcastAudiencesResponse>("/broadcast/audiences");
  },

  /** POST /broadcast/segment-preview — how many contacts a rule set matches now
   *  (live count for the builder; no side effects). */
  segmentPreview(rules: SegmentRuleSet): Promise<{ matched: number }> {
    return apiClient.post<{ matched: number }>("/broadcast/segment-preview", rules);
  },

  /** POST /broadcast/audiences/{id}/segment — save the rule set + rebuild the
   *  audience's membership from the matching contacts (turns it into a dynamic segment). */
  materializeSegment(
    audienceId: string,
    rules: SegmentRuleSet,
  ): Promise<{ audience_id: string; member_count: number; rules: SegmentRuleSet }> {
    return apiClient.post(`/broadcast/audiences/${audienceId}/segment`, rules);
  },

  /** GET /broadcast/templates — reusable campaign blueprints (optionally by category). */
  listTemplates(category?: string): Promise<{ templates: CampaignTemplate[]; count: number }> {
    return apiClient.get("/broadcast/templates", category ? { category } : undefined);
  },

  /** GET /broadcast/analytics — org-wide marketing analytics dashboard. */
  getAnalytics(sinceDays = 30): Promise<MarketingAnalytics> {
    return apiClient.get<MarketingAnalytics>("/broadcast/analytics", { since_days: sinceDays });
  },

  /** POST /broadcast/content/generate — draft N copy variants (stateless, nothing saved). */
  generateContent(input: GenerateContentInput): Promise<GeneratedContent> {
    return apiClient.post<GeneratedContent>("/broadcast/content/generate", input);
  },

  /** GET /broadcast/content/snippets — saved snippets, newest first (optional kind filter). */
  listContentSnippets(kind?: ContentKind): Promise<{ snippets: ContentSnippet[]; count: number }> {
    return apiClient.get("/broadcast/content/snippets", kind ? { kind } : undefined);
  },

  /** POST /broadcast/content/snippets — keep a generated (or hand-written) variant. */
  saveContentSnippet(input: SaveSnippetInput): Promise<ContentSnippet> {
    return apiClient.post<ContentSnippet>("/broadcast/content/snippets", input);
  },

  /** DELETE /broadcast/content/snippets/{id}. */
  deleteContentSnippet(snippetId: string): Promise<{ deleted: boolean; id: string }> {
    return apiClient.delete(`/broadcast/content/snippets/${snippetId}`);
  },

  /** POST /broadcast/templates — create a reusable template. */
  createTemplate(input: CreateTemplateInput): Promise<CampaignTemplate> {
    return apiClient.post<CampaignTemplate>("/broadcast/templates", input);
  },

  /** DELETE /broadcast/templates/{id}. */
  deleteTemplate(templateId: string): Promise<{ deleted: boolean }> {
    return apiClient.delete(`/broadcast/templates/${templateId}`);
  },

  /** POST /broadcast/templates/{id}/create-campaign — start a DRAFT campaign from a template. */
  createCampaignFromTemplate(
    templateId: string,
    input: { name?: string; project_id?: string } = {},
  ): Promise<{ id: string; name: string; status: string; subject: string | null; from_template: string }> {
    return apiClient.post(`/broadcast/templates/${templateId}/create-campaign`, input);
  },

  /** POST /broadcast/audiences/{id}/members — add a contact (idempotent);
   *  returns the refreshed member_count. */
  addAudienceMember(
    audienceId: string,
    contactId: string,
  ): Promise<BroadcastAudienceMemberResult> {
    return apiClient.post<BroadcastAudienceMemberResult>(
      `/broadcast/audiences/${audienceId}/members`,
      { contact_id: contactId },
    );
  },

  /** POST /broadcast/audiences/{id}/add-all-contacts — populate with every
   *  subscribed contact ('email everyone'). */
  addAllContactsToAudience(
    audienceId: string,
  ): Promise<BroadcastAudienceMemberResult> {
    return apiClient.post<BroadcastAudienceMemberResult>(
      `/broadcast/audiences/${audienceId}/add-all-contacts`,
      {},
    );
  },

  /** POST /broadcast/campaigns/{id}/audience — attach (or clear) the audience a
   *  campaign targets. */
  setCampaignAudience(
    campaignId: string,
    audienceId: string | null,
  ): Promise<BroadcastCampaignAudienceResult> {
    return apiClient.post<BroadcastCampaignAudienceResult>(
      `/broadcast/campaigns/${campaignId}/audience`,
      { audience_id: audienceId },
    );
  },

  /** POST /broadcast/campaigns/{id}/dispatch-audience — resolve an audience to
   *  its subscribed contacts and send (unsubscribes always honoured). */
  dispatchToAudience(
    campaignId: string,
    audienceId: string,
    overrideDeliverabilityFloor = false,
  ): Promise<BroadcastDispatchResult> {
    return apiClient.post<BroadcastDispatchResult>(
      `/broadcast/campaigns/${campaignId}/dispatch-audience`,
      { audience_id: audienceId, override_deliverability_floor: overrideDeliverabilityFloor },
    );
  },

  // ── Scheduling (migration 492 — the SKIP-LOCKED claim queue) ──────────────

  /** POST /broadcast/campaigns/{id}/schedule — fire at run_at; rescheduling
   *  replaces the pending send. */
  scheduleCampaign(
    campaignId: string,
    input: BroadcastScheduleInput,
  ): Promise<BroadcastSendJob> {
    return apiClient.post<BroadcastSendJob>(
      `/broadcast/campaigns/${campaignId}/schedule`,
      input,
    );
  },

  /** POST /broadcast/campaigns/{id}/cancel-schedule — cancel the pending send,
   *  return the campaign to DRAFT. */
  cancelSchedule(campaignId: string): Promise<BroadcastCancelScheduleResult> {
    return apiClient.post<BroadcastCancelScheduleResult>(
      `/broadcast/campaigns/${campaignId}/cancel-schedule`,
      {},
    );
  },

  /** GET /broadcast/campaigns/{id}/jobs — send jobs (scheduled / fired / failed). */
  listJobs(campaignId: string): Promise<BroadcastJobsResponse> {
    return apiClient.get<BroadcastJobsResponse>(
      `/broadcast/campaigns/${campaignId}/jobs`,
    );
  },

  /** GET /broadcast/metrics — org KPIs + Command-View 'needs you now' signals. */
  getMetrics(): Promise<BroadcastMetrics> {
    return apiClient.get<BroadcastMetrics>("/broadcast/metrics");
  },

  /** GET /broadcast/campaigns/{id}/readiness — the send-readiness checklist. */
  getReadiness(campaignId: string): Promise<BroadcastReadiness> {
    return apiClient.get<BroadcastReadiness>(
      `/broadcast/campaigns/${campaignId}/readiness`,
    );
  },

  /** GET /broadcast/audiences/{id}/eligibility — would this address receive a
   *  send to this audience, and if not, why (subscribed + membership gates)? */
  checkEligibility(
    audienceId: string,
    email: string,
  ): Promise<BroadcastEligibility> {
    return apiClient.get<BroadcastEligibility>(
      `/broadcast/audiences/${audienceId}/eligibility`,
      { email },
    );
  },

  /** GET /broadcast/campaigns/{id}/report — the post-send report. */
  getReport(campaignId: string): Promise<BroadcastCampaignReport> {
    return apiClient.get<BroadcastCampaignReport>(
      `/broadcast/campaigns/${campaignId}/report`,
    );
  },

  /** POST /broadcast/campaigns/{id}/test-send — preview the campaign in an inbox.
   *  Renders like a real send but records nothing + doesn't advance status. */
  sendTest(campaignId: string, email: string): Promise<{ sent: boolean; to: string; error?: string }> {
    return apiClient.post(`/broadcast/campaigns/${campaignId}/test-send`, { email });
  },

  /** POST /broadcast/campaigns/{id}/content — save the compose editor output. */
  updateCampaignContent(
    campaignId: string,
    body: BroadcastContentUpdate,
  ): Promise<BroadcastCampaign> {
    return apiClient.post<BroadcastCampaign>(
      `/broadcast/campaigns/${campaignId}/content`,
      body,
    );
  },

  /** POST /broadcast/campaigns/{id}/variants — replace the A/B variants. */
  setCampaignVariants(
    campaignId: string,
    variants: BroadcastVariantInput[],
  ): Promise<{ variants: BroadcastVariant[]; count: number }> {
    return apiClient.post(`/broadcast/campaigns/${campaignId}/variants`, { variants });
  },

  /** GET /broadcast/campaigns/{id}/variants — the campaign's A/B variants. */
  listCampaignVariants(
    campaignId: string,
  ): Promise<{ variants: BroadcastVariant[]; count: number }> {
    return apiClient.get(`/broadcast/campaigns/${campaignId}/variants`);
  },

  // ── Automated flows (migration 496) ────────────────────────────────────────

  createFlow(input: {
    name: string;
    description?: string | null;
    trigger_type?: BroadcastFlowTrigger;
    trigger_config?: Record<string, unknown>;
  }): Promise<BroadcastFlow> {
    return apiClient.post<BroadcastFlow>("/broadcast/flows", input);
  },

  listFlows(): Promise<{ flows: BroadcastFlow[]; count: number }> {
    return apiClient.get("/broadcast/flows");
  },

  addFlowStep(
    flowId: string,
    step: { step_order: number; subject?: string | null; body_template?: string | null; delay_hours?: number },
  ): Promise<BroadcastFlowStep> {
    return apiClient.post<BroadcastFlowStep>(`/broadcast/flows/${flowId}/steps`, step);
  },

  listFlowSteps(flowId: string): Promise<{ steps: BroadcastFlowStep[]; count: number }> {
    return apiClient.get(`/broadcast/flows/${flowId}/steps`);
  },

  setFlowStatus(flowId: string, status: string): Promise<BroadcastFlow> {
    return apiClient.post<BroadcastFlow>(`/broadcast/flows/${flowId}/status`, { status });
  },

  enrollInFlow(flowId: string, contactId: string): Promise<Record<string, unknown>> {
    return apiClient.post(`/broadcast/flows/${flowId}/enroll`, { contact_id: contactId });
  },
};
